import React, { useMemo } from 'react';
import { Button } from '@blueprintjs/core';
import { useHorizontalScrollNav } from '../../hooks/useHorizontalScrollNav';
import '../../css/performanceAndScroll.css';

const PerformanceTabSelector = ({
  activeTab,
  onTabChange,
  accountId,
  tabs,
}) => {
  const defaultTabsMemo = useMemo(
    () => [
      {
        key: 'assessment',
        label: 'Assessment',
        type: 'assessment',
      },
      {
        key: 'manager',
        label: 'Review',
        type: 'review',
      },
    ],
    []
  );

  const resolvedTabs = useMemo(
    () =>
      Array.isArray(tabs) && tabs.length > 0 ? tabs : defaultTabsMemo,
    [tabs, defaultTabsMemo]
  );

  const { scrollRef, canScrollLeft, canScrollRight, scrollByDir } = useHorizontalScrollNav({
    arrowsVisibility: 'always',
    layoutDependency: resolvedTabs.length,
    activeItemId: activeTab,
    scrollIntoViewAttr: 'data-tab-key',
    scrollIntoViewEnabled: resolvedTabs.length > 0,
    minScrollChunk: 180,
    scrollViewportRatio: 0.65,
  });

  const getSelfAssessmentIcon = isActive => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="19"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M9.58333 12.5H5.83333C4.94928 12.5 4.10143 12.8512 3.47631 13.4763C2.85119 14.1014 2.5 14.9493 2.5 15.8333V17.5"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.8148 13.8548C18.1468 13.5228 18.3333 13.0726 18.3333 12.6031C18.3333 12.1337 18.1468 11.6834 17.8148 11.3515C17.4829 11.0195 17.0326 10.833 16.5632 10.833C16.0937 10.833 15.6434 11.0195 15.3115 11.3515L11.9698 14.6948C11.7717 14.8928 11.6267 15.1376 11.5482 15.4065L10.8507 17.7981C10.8297 17.8698 10.8285 17.9458 10.847 18.0182C10.8656 18.0906 10.9032 18.1566 10.956 18.2094C11.0088 18.2622 11.0749 18.2999 11.1472 18.3184C11.2196 18.337 11.2956 18.3357 11.3673 18.3148L13.759 17.6173C14.0279 17.5388 14.2726 17.3938 14.4707 17.1956L17.8148 13.8548Z"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.33333 9.16667C10.1743 9.16667 11.6667 7.67428 11.6667 5.83333C11.6667 3.99238 10.1743 2.5 8.33333 2.5C6.49238 2.5 5 3.99238 5 5.83333C5 7.67428 6.49238 9.16667 8.33333 9.16667Z"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const getManagerReviewIcon = isActive => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M20 13C20 18 16.5 20.5 12.34 21.95C12.1222 22.0238 11.8855 22.0203 11.67 21.94C7.5 20.5 4 18 4 13V5.99996C4 5.73475 4.10536 5.48039 4.29289 5.29286C4.48043 5.10532 4.73478 4.99996 5 4.99996C7 4.99996 9.5 3.79996 11.24 2.27996C11.4519 2.09896 11.7214 1.99951 12 1.99951C12.2786 1.99951 12.5481 2.09896 12.76 2.27996C14.51 3.80996 17 4.99996 19 4.99996C19.2652 4.99996 19.5196 5.10532 19.7071 5.29286C19.8946 5.48039 20 5.73475 20 5.99996V13Z"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.37598 18.9102C6.80269 17.7624 7.5704 16.7726 8.576 16.0738C9.5816 15.375 10.777 15.0007 12.0015 15.001C13.2261 15.0013 14.4212 15.3763 15.4265 16.0757C16.4317 16.775 17.1989 17.7652 17.625 18.9132"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15C14.2091 15 16 13.2091 16 11C16 8.79086 14.2091 7 12 7C9.79086 7 8 8.79086 8 11C8 13.2091 9.79086 15 12 15Z"
        stroke={isActive ? '#3B5998' : '#707070'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const getOtherReviewIcon = isActive => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke={isActive ? '#3B5998' : '#707070'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="10" cy="8" r="5" />
      <path d="M2 21a8 8 0 0 1 10.434-7.62" />
      <circle cx="18" cy="18" r="3" />
      <path d="m22 22-1.9-1.9" />
    </svg>
  );

  const getIcon = (tab, isActive) => {
    if (tab.step_type === 2) {
      return getOtherReviewIcon(isActive);
    }

    if (
      tab.type === 'assessment' ||
      tab.type === 'acknowledgement' ||
      tab.key === 'acknowledgement'
    ) {
      return getSelfAssessmentIcon(isActive);
    }

    return getManagerReviewIcon(isActive);
  };

  const scrollTrackStyle = {
    overflowX: 'auto',
    overflowY: 'hidden',
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain',
    boxSizing: 'border-box',
  };

  const tabsRowStyle = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    width: 'max-content',
    minHeight: '40px',
    boxSizing: 'border-box',
    paddingLeft: '4px',
    paddingRight: '40px',
  };

  const arrowBtnStyle = {
    flexShrink: 0,
    flexGrow: 0,
    alignSelf: 'center',
    minWidth: 40,
    width: 40,
    minHeight: 36,
    padding: 0,
    position: 'relative',
    zIndex: 2,
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        borderBottom: '1px solid #ddd',
        marginBottom: '10px',
        marginTop: '10px',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'visible',
          paddingRight: '2px',
        }}
      >
        <Button
          type="button"
          aria-label="Scroll steps left"
          icon="chevron-left"
          minimal
          small
          disabled={!canScrollLeft}
          onClick={() => scrollByDir(-1)}
          style={arrowBtnStyle}
        />
        <div
          ref={scrollRef}
          className="u-hide-scrollbar-x"
          style={scrollTrackStyle}
        >
          <div style={tabsRowStyle}>
            {resolvedTabs?.length > 0 &&
              resolvedTabs.map((tab, index) => {
                const isActive = activeTab === tab.key;

                return (
                  <React.Fragment key={tab.key}>
                    <div
                      data-tab-key={tab.key}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onTabChange(tab.key);
                        }
                      }}
                      onClick={() => onTabChange(tab.key)}
                      style={{
                        cursor: 'pointer',
                        fontFamily: 'Roboto',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: isActive ? '#3B5998' : '#4D4D4D',
                        borderBottom: isActive
                          ? '3px solid #3B5998'
                          : '3px solid transparent',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.3s ease',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getIcon(tab, isActive)}
                      <span style={{ whiteSpace: 'nowrap' }}>{tab.label}</span>
                    </div>

                    {index !== resolvedTabs.length - 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '42px',
                          flexShrink: 0,
                          margin: '0 2px',
                          color: '#B0B0B0',
                          fontSize: '20px',
                          fontWeight: 500,
                          position: 'relative',
                          top: '-1px',
                        }}
                      >
                        →
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
        <Button
          type="button"
          aria-label="Scroll steps right"
          icon="chevron-right"
          minimal
          small
          disabled={!canScrollRight}
          onClick={() => scrollByDir(1)}
          style={arrowBtnStyle}
        />
      </div>
    </div>
  );
};

export default PerformanceTabSelector;
